/**
 * WizardV2 - Main Wizard Orchestrator (v2 Sidebar Layout)
 * 
 * Refactored version with extracted hooks for better maintainability:
 * - useWizardState: State management, navigation, localStorage backup
 * - useReportLoader: Loading existing reports when resuming
 * - useAutoSave: Auto-saving drafts to backend
 * 
 * This is a parallel implementation of the wizard with the new sidebar layout.
 * The original Wizard.tsx remains untouched as a fallback.
 */

import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { useAutoSave } from '../../../hooks/useAutoSave';

// Custom hooks
import { useClaimId, useWizardState, useReportLoader } from './hooks';

// V2 Sidebar Steps
import SidebarStep1 from './SidebarStep1';
import SidebarStep2 from './SidebarStep2';
import SidebarStep3 from './SidebarStep3';
import SidebarStep4 from './SidebarStep4';
import SidebarStep4_FutureClosure from './SidebarStep4_FutureClosure';
import SidebarStep5 from './SidebarStep5';
import SidebarStep5_FutureClosure from './SidebarStep5_FutureClosure';
import SidebarStep6 from './SidebarStep6';
import SidebarStep6_FutureClosure from './SidebarStep6_FutureClosure';
import SidebarStep7 from './SidebarStep7';
import SidebarStep7_FutureClosure from './SidebarStep7_FutureClosure';

export default function WizardV2() {
  // SSoT: Single source of claim identity from URL
  const { claimId, isResolving, error: claimError } = useClaimId();

  // Use extracted state management hook — pass claimId in
  const {
    step,
    data,
    showRestoredBanner,
    highestCompletedStep,
    loadingUrlDraft,
    backendCompletionPercent,
    resumeReportId,
    urlSection,
    setStep,
    setData,
    updateData,
    next,
    back,
    goToStep,
    clearAndStartFresh,
    setShowRestoredBanner,
    setHighestCompletedStep,
  } = useWizardState(claimId);

  // Load report data when resuming
  useReportLoader({
    resumeReportId,
    setData,
    setStep,
    setHighestCompletedStep,
  });

  // Auto-save hook — uses Claims API when claimId is present
  const {
    lastSaved,
    isSaving,
    deleteDraft,
    saveNow
  } = useAutoSave({
    step,
    data,
    enabled: !isResolving && !!claimId,
    claimId,
    reportId: resumeReportId
  });

  // Force-save once when we have data but no draft in DB yet.  This creates
  // the claim document so backend endpoints have something to read.
  //
  // Depend on primitive presence-checks (NOT `data` itself — that object
  // churns on every state update, which would re-fire this effect and keep
  // queuing setTimeout saveNow() calls, contributing to the render cascade).
  const hasForcesSaved = useRef(false);
  const hasClientName = !!data?.client_name;
  const hasFinancialData = !!(data?.financial_data && Object.keys(data.financial_data).length > 0);
  useEffect(() => {
    if (
      !hasForcesSaved.current &&
      !loadingUrlDraft &&
      claimId &&
      (hasClientName || hasFinancialData) &&
      !lastSaved
    ) {
      hasForcesSaved.current = true;
      console.log('🔄 Force-saving: claim exists locally but not in DB');
      setTimeout(() => saveNow(), 2000);
    }
  }, [loadingUrlDraft, claimId, hasClientName, hasFinancialData, lastSaved, saveNow]);

  // SSoT: Claim ID comes from URL — no fallback chain
  const effectiveClaimId = claimId || '';

  // Reset wizard state
  const reset = async () => {
    await deleteDraft();
    setStep(1);
    setData({
      client_name: '',
      claimant_name: '',
      business_address: '',
      zone: '',
      business_type: '',
      financial_data: {}
    });
  };

  // Compute outstanding items from financial data.  Memoized on a stable
  // signature so we don't iterate transactions on every render (and so
  // reviewCount/anomalyCount identities don't churn → every child re-render).
  const { reviewCount, anomalyCount } = useMemo(() => {
    let reviewCount = 0;
    let anomalyCount = 0;

    const financialData = data?.financial_data || {};
    Object.values(financialData).forEach((period: any) => {
      if (period?.transactions) {
        period.transactions.forEach((txn: any) => {
          if (txn.needs_review) reviewCount++;
        });
      }
    });

    const cachedAnomalies = data?.cached_anomaly_analysis?.anomalies || [];
    const resolvedAnomalies = data?.resolved_anomalies || [];
    const resolvedIds = new Set(resolvedAnomalies.map((a: any) => a.id));
    anomalyCount = cachedAnomalies.filter((a: any) => !resolvedIds.has(a.id)).length;

    if (data?.anomaly_count !== undefined) anomalyCount = data.anomaly_count;
    if (data?.review_count !== undefined) reviewCount = data.review_count;

    return { reviewCount, anomalyCount };
  }, [
    data?.financial_data,
    data?.cached_anomaly_analysis?.anomalies,
    data?.resolved_anomalies,
    data?.anomaly_count,
    data?.review_count,
  ]);

  // Stable setData callback for SidebarStep2 (the inline arrow was creating
  // a new function every render and triggering every child re-render).
  const handleStep2DataUpdate = useCallback((updates: any) => {
    setData((prev: any) => ({ ...prev, ...updates }));
  }, [setData]);

  // Common step props.  Memoized so SidebarSteps don't re-render on every
  // WizardV2 render (auto-save ticks update lastSaved which used to cascade
  // through every child component).
  const commonProps = useMemo(() => ({
    data,
    isSaving,
    lastSaved,
    onSave: saveNow,
    goToStep,
    highestCompletedStep,
    claimId: effectiveClaimId,
    // F2-fix1 (2026-04-29): backend SSoT for completion %.  Step components
    // prefer this over their local hardcoded mocks (75/85/100) or per-step
    // heuristics.  Undefined when backend hasn't computed yet — steps
    // fall back via `?? <existing>`.
    backendCompletionPercent,
  }), [data, isSaving, lastSaved, saveNow, goToStep, highestCompletedStep, effectiveClaimId, backendCompletionPercent]);

  // Props for review/final steps that need outstanding item counts
  const reviewProps = useMemo(() => ({
    ...commonProps,
    reviewCount,
    anomalyCount,
  }), [commonProps, reviewCount, anomalyCount]);

  // Render the appropriate step based on current step and calculation_mode
  const renderStep = () => {
    const isFutureClosure = data.calculation_mode === 'future_closure';
    console.log(`🎯 renderStep called: step=${step}, isFutureClosure=${isFutureClosure}`);
    
    switch (step) {
      case 1:
        return (
          <SidebarStep1 
            onNext={next} 
            {...commonProps}
          />
        );
      
      case 2:
        return (
          <SidebarStep2
            onNext={next}
            onBack={back}
            onDataUpdate={handleStep2DataUpdate}
            {...commonProps}
          />
        );
      
      case 3:
        return (
          <SidebarStep3
            onNext={next}
            onBack={back}
            onDataUpdate={updateData}
            initialSection={urlSection}
            {...commonProps}
          />
        );
      
      case 4:
        if (isFutureClosure) {
          return (
            <SidebarStep4_FutureClosure
              onNext={next}
              onBack={back}
              onDataUpdate={updateData}
              {...commonProps}
            />
          );
        }
        return (
          <SidebarStep4
            onNext={next}
            onBack={back}
            onDataUpdate={updateData}
            {...commonProps}
          />
        );
      
      case 5:
        if (isFutureClosure) {
          return (
            <SidebarStep5_FutureClosure
              onNext={next}
              onBack={back}
              onDataUpdate={updateData}
              {...commonProps}
            />
          );
        }
        return (
          <SidebarStep5
            onNext={next}
            onBack={back}
            onDataUpdate={updateData}
            {...commonProps}
          />
        );
      
      case 6:
        if (isFutureClosure) {
          return (
            <SidebarStep6_FutureClosure
              onBack={back}
              onComplete={reset}
              {...reviewProps}
            />
          );
        }
        return (
          <SidebarStep6
            key="step6-stable"
            onBack={back}
            onComplete={reset}
            {...reviewProps}
          />
        );
      
      case 7:
        if (isFutureClosure) {
          return (
            <SidebarStep7_FutureClosure
              onBack={back}
              onComplete={reset}
              {...reviewProps}
            />
          );
        }
        return (
          <SidebarStep7
            onBack={back}
            onComplete={reset}
            {...reviewProps}
          />
        );
      
      default:
        return null;
    }
  };

  // Loading state for URL draft
  if (loadingUrlDraft) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-base)]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[var(--color-brand)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="cs-meta">Loading claim data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Restored Data Banner */}
      {showRestoredBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 p-4 bg-[var(--color-brand)] text-[var(--color-on-brand)] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <p className="font-medium">Previous session restored</p>
              <p className="text-[var(--color-on-brand)]/80 text-sm">Continue where you left off or start fresh.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowRestoredBanner(false)}
              className="px-4 py-2 bg-[var(--color-on-brand)]/20 hover:bg-[var(--color-on-brand)]/30 rounded-lg text-sm font-medium"
              data-testid="continue-session-btn"
            >
              Continue
            </button>
            <button
              onClick={clearAndStartFresh}
              className="px-4 py-2 bg-[var(--color-brand-hover)] hover:bg-[var(--color-needs-human-tint-bd)] rounded-lg text-sm font-medium"
              data-testid="start-fresh-btn"
            >
              Start Fresh
            </button>
          </div>
        </div>
      )}
      
      {/* Main wizard content */}
      <div className={showRestoredBanner ? 'pt-20' : ''}>
        {renderStep()}
      </div>
    </>
  );
}
